import { z } from 'zod';
import { CS_PERIOD_LENGTH } from '@lp/features/Scheduler/SchedulerClinicalSpecialists/period.utils';
import {
  CSAtHubWeeklyRequirementsInputsSettings,
  CSDeductionShiftRule,
  CSDeductionShiftRulesSettings,
  CSPeriodResourceRulesSettings,
  CSRequirementsInputsSettings,
  CSResourceRulesSettings,
  CSShiftTimeGroup,
  CSTimeGroupRequirementsInputsSettings,
  OrganTypeComboMinRules,
  OrganTypeTimeGroupComboMinRules,
  Region,
  TimeGroupComboMinRule,
} from '@/api/apis/resources/schedule';
import {
  CSSchedulerAtHubWeeklySettingsInputsFormType,
  CSSchedulerSettingsDeductionRulesFormType,
  CSSchedulerSettingsInputsFormType,
  CSSchedulerSettingsPeriodRulesFormType,
  CSSchedulerSettingsRulesFormType,
  CSSchedulerSettingsTimeGroupInputsFormType,
} from './types';
import { DayOfWeek } from '@/types/date.types';
import { OrganType, OrganTypeWithoutKidney } from '@/api/apis/case';
import { addCustomZodErrors, getKeys } from '@/utils/helpers';

/** The Absence Deduction Table always has one row per absence-day count in a period. */
export const CS_DEDUCTION_RULE_ABSENCE_DAYS = Array.from({ length: CS_PERIOD_LENGTH + 1 }, (_, days) => days);

/**
 * The weekend columns are bounded by the number of Saturdays and Sundays in a period, which is a
 * different quantity from the period length - derive it rather than hard-coding 8.
 */
const CS_WEEKEND_DAYS_PER_PERIOD = (CS_PERIOD_LENGTH / 7) * 2;

/** MONTHLY FAMILY */

const getInitialComboMinRule = () => {
  return getKeys(OrganType).reduce((acc, o) => {
    return { ...acc, [o]: { flex: 0, strict: 0 } };
  }, {} as OrganTypeComboMinRules);
};

export const getDefaultCSSchedulerInputsValues = (
  regionList: Region[],
  data: CSRequirementsInputsSettings,
): CSSchedulerSettingsInputsFormType => {
  const hubList = regionList.flatMap((r) => r.hubs);
  const perRegions: Record<string, OrganTypeComboMinRules> = regionList.reduce((acc, r) => {
    return { ...acc, [r.id]: data.perRegions[r.id] ?? getInitialComboMinRule() };
  }, {});
  const perHubs: Record<string, OrganTypeComboMinRules> = hubList.reduce((acc, h) => {
    return { ...acc, [h.id]: data.perHubs[h.id] ?? getInitialComboMinRule() };
  }, {});
  const atHubDays: Record<string, string[]> = hubList.reduce((acc, h) => {
    return { ...acc, [h.id]: data.atHubDays[h.id] ?? [] };
  }, {});

  return {
    ...data,
    perHubs,
    perRegions,
    atHubDays,
  };
};

export const getDefaultCSSchedulerRulesValues = (data: CSResourceRulesSettings): CSSchedulerSettingsRulesFormType => {
  return data;
};

const numberSchema = z.number({ message: 'Required' }).min(0);

const comboMinRuleSchema = z.object({
  strict: numberSchema,
  flex: numberSchema,
});

// TMDXLP-14091: Kidney inputs are hidden from CS Settings and the API omits the `Kidney` key
// entirely for hubs/regions. Fall back to a zeroed entry (never blocks Save) — it's stripped
// from the payload on submit anyway via `omitKidneyComboRules`.
const inputsServiceRecordSchema = z.record(z.string(), z.record(z.enum(OrganTypeWithoutKidney), comboMinRuleSchema));

export const csSchedulerSettingsInputsFormSchema = z.object({
  exactlyDailyPhoneSupport: z.record(z.enum(OrganTypeWithoutKidney), numberSchema),
  perRegions: inputsServiceRecordSchema,
  perHubs: inputsServiceRecordSchema,
  atHubDays: z.record(z.string(), z.array(z.enum(DayOfWeek))),
});

const vacationDeductionItemSchema = z.object({
  each: numberSchema,
  deduction: numberSchema,
});

const onCallInRowSchema = z.object({
  hardMin: numberSchema,
  recommendedMin: numberSchema,
  recommendedMax: numberSchema,
  hardMax: numberSchema,
});

export const csSchedulerSettingsRulesFormSchema = z.object({
  shiftPerPeriods: z.object({
    lead: z.object({
      onCall: numberSchema,
      onCallOnWeekend: numberSchema,
      maxAtHub: numberSchema,
      maxPhoneSupport: numberSchema,
      onCallAndPhoneSupport: numberSchema,
    }),
    specialist: z.object({
      onCall: numberSchema,
      onCallOnWeekend: numberSchema,
      maxAtHub: numberSchema,
    }),
  }),
  minimumWeekends: numberSchema,
  noCallDaysPerPeriod: numberSchema,
  onCallInRow: onCallInRowSchema,
  vacationDeduction: z.object({
    q1_3: vacationDeductionItemSchema,
    q4: vacationDeductionItemSchema,
  }),
  holidayNoCallBonus: numberSchema,
});

/** PERIOD FAMILY */

const getTimeGroupComboMinRule = () => {
  return Object.values(CSShiftTimeGroup).reduce((acc, tg) => {
    return { ...acc, [tg]: { flex: 0, strict: 0 } };
  }, {} as TimeGroupComboMinRule);
};

// TMDXLP-14091: Kidney inputs are hidden from CS (Clinical Specialists) Settings and the API omits the `Kidney` key entirely for regions. Fall back to a zeroed entry (never blocks Save) — it's stripped from the payload on submit anyway via `omitKidneyOrganRules`.
const getInitialOrganRules = () => {
  return Object.values(OrganTypeWithoutKidney).reduce((acc, o) => {
    return { ...acc, [o]: getTimeGroupComboMinRule() };
  }, {} as OrganTypeTimeGroupComboMinRules);
};

export const getDefaultCSSchedulerTimeGroupInputsValues = (
  regionList: Region[],
  data: CSTimeGroupRequirementsInputsSettings,
): CSSchedulerSettingsTimeGroupInputsFormType => {
  const hubList = regionList.flatMap((r) => r.hubs);
  const perRegions: Record<string, OrganTypeTimeGroupComboMinRules> = regionList.reduce((acc, r) => {
    return { ...acc, [r.id]: data.perRegions[r.id] ?? getInitialOrganRules() };
  }, {});
  const perHubs: Record<string, OrganTypeTimeGroupComboMinRules> = hubList.reduce((acc, h) => {
    return { ...acc, [h.id]: data.perHubs[h.id] ?? getInitialOrganRules() };
  }, {});

  return {
    ...data,
    perHubs,
    perRegions,
  };
};

export const getDefaultCSSchedulerAtHubWeeklyInputsValues = (
  regionList: Region[],
  data: CSAtHubWeeklyRequirementsInputsSettings,
): CSSchedulerAtHubWeeklySettingsInputsFormType => {
  const hubList = regionList.flatMap((r) => r.hubs);
  const atHubDays: Record<string, string[]> = hubList.reduce((acc, h) => {
    return { ...acc, [h.id]: data.atHubDays[h.id] ?? [] };
  }, {});

  return { ...data, atHubDays };
};

export const getDefaultCSSchedulerPeriodRulesValues = (
  data: CSPeriodResourceRulesSettings,
): CSSchedulerSettingsPeriodRulesFormType => {
  return data;
};

const timeGroupComboMinRuleSchema = z.record(z.enum(CSShiftTimeGroup), comboMinRuleSchema);

// TMDXLP-14091
const timeGroupInputsServiceRecordSchema = z.record(
  z.string(),
  z.record(z.enum(OrganTypeWithoutKidney), timeGroupComboMinRuleSchema),
);

// `exactlyDailyPhoneSupport` is deliberately absent: it is optional and obsolete on the period
// contract, since PhoneSupport is retired on the period planning path.
export const csSchedulerSettingsTimeGroupInputsFormSchema = z.object({
  perRegions: timeGroupInputsServiceRecordSchema,
  perHubs: timeGroupInputsServiceRecordSchema,
});

export const csSchedulerAtHubWeeklySettingsInputsFormSchema = z.object({
  atHubDays: z.record(z.string(), z.array(z.enum(DayOfWeek))),
});

export const csSchedulerSettingsPeriodRulesFormSchema = z.object({
  onCallInRow: onCallInRowSchema,
  holidayNoCallBonus: numberSchema,
});

export const getDefaultCSSchedulerDeductionRulesValues = (
  data: CSDeductionShiftRulesSettings,
): CSSchedulerSettingsDeductionRulesFormType => {
  return {
    byAbsenceDays: CS_DEDUCTION_RULE_ABSENCE_DAYS.map((absenceDays) => data?.byAbsenceDays?.[absenceDays] ?? {}),
  };
};

// Converts the form's array representation back into the API's Record<number, CSDeductionShiftRule> shape.
export const toCSDeductionShiftRulesSettings = (
  formData: CSSchedulerSettingsDeductionRulesFormType,
): CSDeductionShiftRulesSettings => {
  return {
    byAbsenceDays: formData.byAbsenceDays.reduce(
      (acc, rule, absenceDays) => ({ ...acc, [absenceDays]: rule }),
      {} as Record<number, CSDeductionShiftRule>,
    ),
  };
};

const deductionShiftRuleFullPeriodSchema = numberSchema.max(CS_PERIOD_LENGTH);
const deductionShiftRuleWeekendSchema = numberSchema.max(CS_WEEKEND_DAYS_PER_PERIOD);

const csDeductionShiftRuleSchema = z.object({
  onCall: deductionShiftRuleFullPeriodSchema,
  weekendOnCall: deductionShiftRuleWeekendSchema,
  atHub: deductionShiftRuleFullPeriodSchema,
  officeDay: deductionShiftRuleFullPeriodSchema,
  noCall: deductionShiftRuleFullPeriodSchema,
  weekendOff: deductionShiftRuleWeekendSchema,
});

// Per TMDXLP-13787: Total = On Call + At Hub + Office Day (Admin Day) + No Call + Weekend Off + Absence Days
// (Weekend On Call is excluded) and must always equal CS_PERIOD_LENGTH.
export const getDeductionRuleTotal = (
  rule: Pick<CSDeductionShiftRule, 'onCall' | 'atHub' | 'officeDay' | 'noCall' | 'weekendOff'>,
  absenceDays: number,
) => rule.onCall + rule.atHub + rule.officeDay + rule.noCall + rule.weekendOff + absenceDays;

export const csSchedulerSettingsDeductionRulesFormSchema = z
  .object({
    byAbsenceDays: z.array(csDeductionShiftRuleSchema).length(CS_DEDUCTION_RULE_ABSENCE_DAYS.length),
  })
  .superRefine((data, ctx) => {
    data.byAbsenceDays.forEach((rule, absenceDays) => {
      const total = getDeductionRuleTotal(rule, absenceDays);

      if (total !== CS_PERIOD_LENGTH) {
        // Attached to a synthetic "_total" field (not rendered by any input) so the mismatch
        // blocks saving without putting an error border on one of the visible fields.
        addCustomZodErrors({
          ctx,
          message: `Total (${total}) must equal ${CS_PERIOD_LENGTH}`,
          path: ['byAbsenceDays', absenceDays, '_total'],
        });
      }

      // A weekend On-Call day IS an On-Call day, so it cannot exceed the period total.
      // The server rejects this too; catching it here keeps the error on the offending field.
      if (rule.weekendOnCall > rule.onCall) {
        addCustomZodErrors({
          ctx,
          message: `Weekend On Call (${rule.weekendOnCall}) cannot exceed On Call (${rule.onCall})`,
          path: ['byAbsenceDays', absenceDays, 'weekendOnCall'],
        });
      }
    });
  });
